import { useEffect, useMemo, useState } from 'react';
import { Search, Sparkles, Trash2 } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/Table';
import { toast } from '@/hooks/useToast';
import { ErrorBanner, useDebounced } from '../borrow/ui';
import { searchMedicines } from '../api';
import type { MedicineSearchResult } from '../types';
import NewIndentDialog from './NewIndentDialog';
import { useSaveStockLevels, useStockLevels } from './useIndents';
import type { LevelChange, StockLevelRow, TransferStore } from './types';

type Draft = { min: string; reorder: string; max: string };
const toDraft = (r: Pick<StockLevelRow, 'min_level' | 'reorder_qty' | 'max_level'>): Draft =>
  ({ min: String(r.min_level), reorder: r.reorder_qty == null ? '' : String(r.reorder_qty), max: r.max_level == null ? '' : String(r.max_level) });
const medName = (m: { generic_name: string; strength: string | null; brand_name: string | null }) =>
  [m.generic_name, m.strength, m.brand_name ? `(${m.brand_name})` : null].filter(Boolean).join(' ');

/**
 * Reorder levels for one store: per medicine, the reorder point (min), how much
 * to ask for (reorder qty) and the most to hold (max) — with what the store
 * has, what is on its way, and what an indent would ask for now. Editing needs
 * admin:pharmacy-stores; everyone else reads. §8.3 of the design doc.
 */
export default function ReorderLevelsView({ stores, storeId, receivingStoreId, onStoreChange }: {
  stores: TransferStore[];
  storeId: number | null;
  /** The Main Store — the usual supplier of the indent raised from here. */
  receivingStoreId?: number;
  onStoreChange: (id: number) => void;
}) {
  const { user } = useAuth();
  const canEdit = !!user?.modules?.includes('admin:pharmacy-stores');
  const [belowOnly, setBelowOnly] = useState(false);
  const levels = useStockLevels(storeId, belowOnly);
  const save = useSaveStockLevels();
  const [drafts, setDrafts] = useState<Record<number, Draft>>({});
  const [removed, setRemoved] = useState<Set<number>>(new Set());
  const [added, setAdded] = useState<Array<{ medicine_id: number; name: string }>>([]);
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<MedicineSearchResult[]>([]);
  const [formError, setFormError] = useState('');
  const [indenting, setIndenting] = useState(false);
  const debounced = useDebounced(search.trim());
  const store = stores.find((s) => s.id === storeId);
  const rows = useMemo(() => levels.data ?? [], [levels.data]);

  useEffect(() => { setDrafts({}); setRemoved(new Set()); setAdded([]); }, [storeId]);
  useEffect(() => {
    if (debounced.length < 2) { setResults([]); return; }
    let live = true;
    searchMedicines(debounced).then((r) => { if (live) setResults(r); }).catch(() => { if (live) setResults([]); });
    return () => { live = false; };
  }, [debounced]);

  const setField = (id: number, base: Draft, k: keyof Draft, v: string) => setDrafts((d) => ({ ...d, [id]: { ...(d[id] ?? base), [k]: v } }));
  const dirtyRows = rows.filter((r) => {
    const d = drafts[r.medicine_id];
    if (!d) return false;
    const o = toDraft(r);
    return d.min !== o.min || d.reorder !== o.reorder || d.max !== o.max;
  });
  const changeCount = dirtyRows.length + removed.size + added.length;
  const num = (v: string) => (v.trim() === '' ? null : Number(v));

  const submit = () => {
    if (!storeId) return;
    setFormError('');
    const changes: LevelChange[] = [];
    for (const r of dirtyRows) {
      if (removed.has(r.medicine_id)) continue;
      const d = drafts[r.medicine_id]!;
      changes.push({ medicine_id: r.medicine_id, min_level: Number(d.min), reorder_qty: num(d.reorder), max_level: num(d.max) });
    }
    for (const a of added) {
      const d = drafts[a.medicine_id];
      if (!d || d.min.trim() === '') return setFormError(`Enter a minimum for ${a.name}.`);
      changes.push({ medicine_id: a.medicine_id, min_level: Number(d.min), reorder_qty: num(d.reorder), max_level: num(d.max) });
    }
    for (const id of removed) changes.push({ medicine_id: id, remove: true });
    save.mutate({ storeId, levels: changes }, {
      onSuccess: () => { toast({ title: 'Reorder levels saved', variant: 'success' }); setDrafts({}); setRemoved(new Set()); setAdded([]); },
    });
  };

  const addMedicine = (m: MedicineSearchResult) => {
    if (!rows.some((r) => r.medicine_id === m.id) && !added.some((a) => a.medicine_id === m.id)) {
      setAdded((a) => [...a, { medicine_id: m.id, name: medName(m) }]);
      setDrafts((d) => ({ ...d, [m.id]: { min: '', reorder: '', max: '' } }));
    }
    setSearch('');
    setResults([]);
  };

  const numInput = (id: number, base: Draft, k: keyof Draft, label: string, disabled: boolean) => (
    <Input type="number" min={0} inputMode="numeric" className="w-20" aria-label={label} disabled={disabled}
      value={(drafts[id] ?? base)[k]} onChange={(e) => setField(id, base, k, e.target.value)} />
  );
  const suggestions = rows.filter((r) => r.suggested_qty).length;

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <label className="inline-flex items-center gap-2 text-[13px]">
          <span className="font-semibold">Store</span>
          <select className="h-9 w-auto min-w-[180px] rounded-[var(--radius)] border border-input bg-card px-2 text-[13px]"
            value={storeId ?? ''} onChange={(e) => onStoreChange(Number(e.target.value))}>
            {stores.filter((s) => s.active).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="inline-flex items-center gap-2 text-[13px] text-muted-foreground">
          <input type="checkbox" checked={belowOnly} onChange={(e) => setBelowOnly(e.target.checked)} />
          Only what needs ordering
        </label>
        <span className="flex-1" />
        {suggestions > 0 && stores.filter((s) => s.active).length > 1 && (
          <Button variant="ghost" onClick={() => setIndenting(true)}><Sparkles className="h-4 w-4" /> Raise indent for {suggestions} low item{suggestions === 1 ? '' : 's'}</Button>
        )}
      </div>
      {store?.can_receive && (
        <p className="mb-3 text-[12.5px] text-muted-foreground">
          {store.name} receives from vendors: when it is low, order on a purchase order in 🚚 Procurement using the suggested quantity.
        </p>
      )}

      <div className="overflow-x-auto">
        <Table className="min-w-[860px]">
          <TableHeader>
            <TableRow>
              {['Medicine', 'On hand', 'Coming', 'Min', 'Reorder qty', 'Max', 'Status', canEdit ? '' : null].filter((h) => h !== null).map((h, i) => <TableHead key={i}>{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {levels.isLoading ? (
              <TableRow><TableCell colSpan={8} className="p-6 text-center">Loading…</TableCell></TableRow>
            ) : !rows.length && !added.length ? (
              <TableRow><TableCell colSpan={8} className="p-6 text-center text-muted-foreground">
                {belowOnly ? 'Nothing at this store needs ordering.' : 'No reorder levels set for this store yet.'}
                {canEdit && !belowOnly ? ' Add a medicine below.' : ''}
              </TableCell></TableRow>
            ) : (
              <>
                {rows.map((r) => {
                  const base = toDraft(r);
                  const gone = removed.has(r.medicine_id);
                  return (
                    <TableRow key={r.medicine_id} className={gone ? 'opacity-50' : undefined}>
                      <TableCell className="font-semibold">{medName(r)}</TableCell>
                      <TableCell className="tabular-nums">{r.on_hand}</TableCell>
                      <TableCell className="tabular-nums text-muted-foreground" title="In transit to this store + still owed on open indents">
                        {r.in_transit_in + r.open_indent_qty || '—'}
                      </TableCell>
                      <TableCell>{canEdit ? numInput(r.medicine_id, base, 'min', `Minimum for ${r.generic_name}`, gone) : r.min_level}</TableCell>
                      <TableCell>{canEdit ? numInput(r.medicine_id, base, 'reorder', `Reorder quantity for ${r.generic_name}`, gone) : (r.reorder_qty ?? '—')}</TableCell>
                      <TableCell>{canEdit ? numInput(r.medicine_id, base, 'max', `Maximum for ${r.generic_name}`, gone) : (r.max_level ?? '—')}</TableCell>
                      <TableCell>
                        {r.suggested_qty
                          ? <Badge variant="warn">Order {r.suggested_qty}</Badge>
                          : r.below_min ? <Badge variant="info">Low — on its way</Badge> : <Badge variant="success">OK</Badge>}
                      </TableCell>
                      {canEdit && (
                        <TableCell>
                          <Button variant="ghost" size="sm" aria-label={gone ? 'Keep this level' : 'Remove this level'}
                            onClick={() => setRemoved((s) => { const n = new Set(s); if (n.has(r.medicine_id)) n.delete(r.medicine_id); else n.add(r.medicine_id); return n; })}>
                            {gone ? 'Undo' : <Trash2 className="h-4 w-4" />}
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
                {added.map((a) => {
                  const base = { min: '', reorder: '', max: '' };
                  return (
                    <TableRow key={`new-${a.medicine_id}`}>
                      <TableCell className="font-semibold">{a.name} <Badge variant="neutral" className="ml-1">New</Badge></TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell className="text-muted-foreground">—</TableCell>
                      <TableCell>{numInput(a.medicine_id, base, 'min', `Minimum for ${a.name}`, false)}</TableCell>
                      <TableCell>{numInput(a.medicine_id, base, 'reorder', `Reorder quantity for ${a.name}`, false)}</TableCell>
                      <TableCell>{numInput(a.medicine_id, base, 'max', `Maximum for ${a.name}`, false)}</TableCell>
                      <TableCell />
                      <TableCell>
                        <Button variant="ghost" size="sm" aria-label="Remove" onClick={() => setAdded((x) => x.filter((y) => y !== a))}><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </>
            )}
          </TableBody>
        </Table>
      </div>

      {canEdit && (
        <div className="mt-3 space-y-2">
          <div className="relative max-w-[360px]">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Add a medicine to this store’s levels" className="pl-8" />
          </div>
          {results.length > 0 && (
            <div className="max-h-[160px] max-w-[480px] overflow-y-auto rounded-[var(--radius)] border border-border">
              {results.map((m) => (
                <button key={m.id} type="button" onClick={() => addMedicine(m)}
                  className="block w-full border-b border-border px-3 py-2 text-left text-[13px] last:border-b-0 hover:bg-muted">
                  {medName(m)}
                </button>
              ))}
            </div>
          )}
          <ErrorBanner message={formError || (save.error instanceof Error ? save.error.message : null)} />
          <div className="flex justify-end gap-2">
            {changeCount > 0 && <Button variant="ghost" onClick={() => { setDrafts({}); setRemoved(new Set()); setAdded([]); }}>Discard</Button>}
            <Button disabled={!changeCount} loading={save.isPending} onClick={submit}>
              {changeCount ? `Save ${changeCount} change${changeCount === 1 ? '' : 's'}` : 'No changes'}
            </Button>
          </div>
        </div>
      )}
      <p className="mt-3 text-[12px] text-muted-foreground">
        Min is the reorder point: at or below it the store is low. A suggestion tops up to Max when one is set, otherwise asks for the Reorder qty.
        “Coming” is stock already on its way (transfers in transit, open indents), so the same shortfall is never asked for twice.
      </p>

      {indenting && storeId && (
        <NewIndentDialog
          stores={stores}
          defaultRequestingId={storeId}
          defaultSupplyingId={receivingStoreId !== storeId ? receivingStoreId : undefined}
          prefillSuggestion
          onClose={() => setIndenting(false)}
          onCreated={(ind) => { setIndenting(false); toast({ title: `${ind.indent_number} sent to ${ind.supplying_store_name}`, variant: 'success' }); }}
        />
      )}
    </Card>
  );
}
